import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Catches any uncaught React render error and shows a friendly recovery UI
 * instead of unmounting the whole tree (which would leave the browser tab blank).
 * Log the error to the console so we can diagnose from browser devtools.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary] caught error:", error, info?.componentStack);
  }

  handleReload = () => { window.location.reload(); };
  handleHome    = () => { window.location.href = "/"; };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div data-testid="error-boundary-fallback"
           className="min-h-screen bg-slate-50 grid place-items-center px-6 py-12">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 grid place-items-center">
            <AlertTriangle className="w-6 h-6 text-amber-700"/>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-4">
            Something went wrong on this page
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            The page hit an unexpected error. Reloading usually fixes it.
            If it keeps happening, let the admin know what you were doing right before this.
          </p>
          {this.state.error?.message && (
            <pre className="mt-4 text-[11px] text-left text-slate-500 bg-slate-50 border border-slate-200 rounded p-3 overflow-x-auto whitespace-pre-wrap">
              {String(this.state.error.message).slice(0, 300)}
            </pre>
          )}
          <div className="mt-6 flex items-center justify-center gap-2">
            <button data-testid="error-boundary-reload" onClick={this.handleReload}
                    className="inline-flex items-center gap-2 bg-sky-700 hover:bg-sky-800 text-white text-sm font-medium px-4 py-2 rounded transition-colors">
              <RefreshCw className="w-4 h-4"/>Reload page
            </button>
            <button data-testid="error-boundary-home" onClick={this.handleHome}
                    className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-100 text-slate-700 text-sm font-medium px-4 py-2 rounded transition-colors">
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
